import type { ReportPeriod } from '../../../utils/report-period.utils';
import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import {
  ReportIds,
  ReportInputParameterKeys,
  ReportNtpDisplayModes,
  ReportingApiHttpQuery,
  type AgencyBrandingOptionValue,
  reportSupportsInputParameter,
  type ReportId,
  type ReportingComparisonScope,
} from 'ag-common-lib/lib/contracts/reporting-api.contracts';
import {
  ReportDefaultSettings,
  ReportDefaultSettingsKeys,
} from 'ag-common-lib/lib/models/reporting/report-default-settings.model';
import { getReportUnavailableReason } from '../../report-unavailable/report-unavailable.component';
import { ReportDefaultSettingsService } from '../../../services/report-default-settings.service';
import { ReportingApiService } from '../../../services/reporting-api.service';
import { PRODUCTION_PRECONFIGURED_REPORTS } from './production-reports.config';
import {
  getProductionReportBooleanDefaults,
  PRODUCTION_REPORT_INCLUDE_THRESHOLD_DEFAULT,
} from './production-reports.config.shared';
import {
  ProductionReportConfig,
  ProductionReportInputVariables,
  ProductionReportTemplateSettings,
  ProductionReportsState,
} from './production-reports.models';

const getGeneratedDate = (input: Date = new Date()): string =>
  input.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const formatAsDateInputValue = (value: unknown): string => {
  if (value === undefined || value === null || value === '') {
    return '';
  }

  const date = typeof value === 'number' ? new Date(value) : new Date(String(value));
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const buildDefaultVariables = (report: ProductionReportConfig): ProductionReportInputVariables =>
  report.variables.reduce<ProductionReportInputVariables>((acc, variable) => {
    if (variable.type === 'date') {
      acc[variable.name] = formatAsDateInputValue(variable.defaultValue);
      return acc;
    }

    acc[variable.name] = variable.defaultValue ?? '';
    return acc;
  }, {});

const cloneTemplate = (template: ProductionReportTemplateSettings): ProductionReportTemplateSettings => ({
  title: '',
  subtitle: '',
  rowsPerPage: template.rowsPerPage,
  logoUrl: template.logoUrl,
});

@Injectable()
export class ProductionReportsService {
  private readonly reportingApiService = inject(ReportingApiService);
  private readonly reportDefaultSettingsService = inject(ReportDefaultSettingsService);

  private readonly reports = PRODUCTION_PRECONFIGURED_REPORTS;

  private readonly state = signal<ProductionReportsState>({
    reports: this.reports,
    selectedReportId: null,
    brandingId: null,
    variables: {},
    templateDraft: null,
    generatedDate: getGeneratedDate(),
    pdfBlob: null,
    isLoading: false,
    errorMessage: null,
    unavailableReason: null,
    healthStatus: null,
  });

  public readonly stateSig = this.state.asReadonly();
  public readonly selectedReportSig = computed(
    () => this.reports.find(report => report.id === this.stateSig().selectedReportId) ?? null,
  );

  constructor() {}

  public init = (): void => {
    // Keep initial "no selection" state to mirror the original UI flow.
  };

  public selectReport = (reportId: string): void => {
    const report = this.reports.find(item => item.id === reportId);
    if (!report) {
      return;
    }

    this.patchState({
      selectedReportId: report.id,
      brandingId: null,
      variables: buildDefaultVariables(report),
      templateDraft: cloneTemplate(report.template),
      generatedDate: getGeneratedDate(),
      pdfBlob: null,
      isLoading: true,
      errorMessage: null,
      unavailableReason: null,
    });
  };

  public updateVariable = (name: ReportInputParameterKeys, value: unknown): void => {
    const state = this.stateSig();
    this.patchState({
      variables: {
        ...state.variables,
        [name]: value,
      },
    });
  };

  public updateBrandingId = (brandingId: AgencyBrandingOptionValue): void => {
    this.patchState({ brandingId });
  };

  public finishReportSetup(): void {
    this.patchState({ isLoading: false });
  }

  public updateTemplateDraft = (patch: Partial<ProductionReportTemplateSettings>): void => {
    const currentTemplate = this.stateSig().templateDraft;
    if (!currentTemplate) {
      return;
    }

    this.patchState({
      templateDraft: {
        ...currentTemplate,
        ...patch,
      },
    });
  };

  public setGeneratedDate = (rawValue: string | null): void => {
    if (!rawValue) {
      return;
    }

    const parsedDate = new Date(rawValue);
    if (Number.isNaN(parsedDate.getTime())) {
      return;
    }

    this.patchState({
      generatedDate: getGeneratedDate(parsedDate),
    });
  };

  public checkHealth = async (): Promise<void> => {
    this.patchState({ isLoading: true, errorMessage: null });
    try {
      const healthStatus = await this.reportingApiService.getHealth();
      this.patchState({ healthStatus, isLoading: false });
    } catch (error) {
      this.patchState({
        healthStatus: { ok: false },
        isLoading: false,
        errorMessage: await this.getErrorMessage(error),
      });
    }
  };

  public loadSelectedReport = async (
    options: { scope?: ReportingComparisonScope; mga?: string[]; rmd?: string[] } = {},
  ): Promise<void> => {
    const state = this.stateSig();
    const selectedReport = this.selectedReportSig();
    if (!selectedReport) {
      return;
    }

    this.patchState({
      isLoading: true,
      pdfBlob: null,
      errorMessage: null,
      unavailableReason: null,
    });

    try {
      const startDate = this.getRequiredDateVariableValue(
        state.variables[ReportInputParameterKeys.startDate],
        'start_date',
      );
      const endDate = this.getRequiredDateVariableValue(state.variables[ReportInputParameterKeys.endDate], 'end_date');
      const rowsPerPage = this.getRequiredPositiveIntegerValue(
        state.templateDraft?.rowsPerPage ?? selectedReport.template.rowsPerPage,
        'rows_per_page',
      );
      const displayThreshold = this.getOptionalNumberVariableValue(
        state.variables[ReportInputParameterKeys.displayThreshold],
        'display_threshold',
      );
      const includeThreshold = this.isThresholdIncluded(state.variables);
      const supportsNtp = reportSupportsInputParameter(selectedReport.id, ReportInputParameterKeys.ntpDisplayMode);
      const ntpDisplayMode = supportsNtp
        ? this.getNtpDisplayMode(state.variables[ReportInputParameterKeys.ntpDisplayMode])
        : undefined;
      // Only personal reports carry this variable: MGA and RMD rows are not agents.
      const supportsSuppression = reportSupportsInputParameter(
        selectedReport.id,
        ReportInputParameterKeys.showSuppressedAgents,
      );
      const showSuppressedAgents = supportsSuppression
        ? this.getRequiredBooleanVariableValue(
            state.variables[ReportInputParameterKeys.showSuppressedAgents],
            'show_suppressed_agents',
          )
        : undefined;
      const supportsTotals = reportSupportsInputParameter(selectedReport.id, ReportInputParameterKeys.showTotals);
      const showTotals = supportsTotals
        ? this.getRequiredBooleanVariableValue(state.variables[ReportInputParameterKeys.showTotals], 'show_totals')
        : undefined;
      const title = this.getOptionalStringValue(state.templateDraft?.title ?? selectedReport.template.title);
      const subtitle = this.getOptionalStringValue(state.templateDraft?.subtitle ?? selectedReport.template.subtitle);
      const query: ReportingApiHttpQuery = {
        report_id: selectedReport.id,
        start_date: startDate,
        end_date: endDate,
        ...(options.scope ? { scope: options.scope } : {}),
        ...(options.mga?.length ? { mga: options.mga } : {}),
        ...(options.rmd?.length ? { rmd: options.rmd } : {}),
        ...(title !== undefined
          ? {
              title,
            }
          : {}),
        ...(subtitle !== undefined
          ? {
              subtitle,
            }
          : {}),
        rows_per_page: rowsPerPage,
        ...(typeof state.brandingId === 'number' ? { branding_id: state.brandingId } : {}),
        ...(ntpDisplayMode !== undefined ? { ntp_display_mode: ntpDisplayMode } : {}),
        ...(showSuppressedAgents !== undefined ? { show_suppressed_agents: showSuppressedAgents } : {}),
        ...(showTotals !== undefined ? { show_totals: showTotals } : {}),
        include_threshold: includeThreshold,
        ...(displayThreshold !== undefined
          ? {
              display_threshold: displayThreshold,
            }
          : {}),
      };

      const pdfBlob = await this.reportingApiService.getReportPdf(query);

      this.patchState({
        pdfBlob,
        generatedDate: getGeneratedDate(),
        isLoading: false,
      });
    } catch (error) {
      this.patchState({
        isLoading: false,
        pdfBlob: null,
        errorMessage: await this.getErrorMessage(error),
        unavailableReason: getReportUnavailableReason(error),
      });
    }
  };

  public resetSelectedReport = (): void => {
    const state = this.stateSig();
    if (!state.selectedReportId) {
      return;
    }

    this.selectReport(state.selectedReportId);
  };

  public applySavedDefaultSettings = async (reportId: ReportId): Promise<ReportDefaultSettings | null> => {
    const settings = await this.loadSavedDefaultSettings(reportId);
    const currentTemplate = this.stateSig().templateDraft;
    const booleanDefaults = getProductionReportBooleanDefaults(reportId);

    if (!currentTemplate) {
      return settings;
    }

    if (!settings) {
      this.patchState({ variables: { ...this.stateSig().variables, ...booleanDefaults } });
      return null;
    }

    // Settings saved before suppression became personal-only still carry the key for
    // MGA and RMD reports; those reports no longer have the variable, so the stored
    // value is ignored rather than re-added to a form that has nowhere to show it.
    const supportsNtp = reportSupportsInputParameter(reportId, ReportInputParameterKeys.ntpDisplayMode);
    const supportsSuppression = reportSupportsInputParameter(reportId, ReportInputParameterKeys.showSuppressedAgents);

    const variables = {
      ...this.stateSig().variables,
      [ReportInputParameterKeys.displayThreshold]: settings[ReportDefaultSettingsKeys.displayThreshold],
      [ReportInputParameterKeys.includeThreshold]:
        settings[ReportDefaultSettingsKeys.includeThreshold] ?? PRODUCTION_REPORT_INCLUDE_THRESHOLD_DEFAULT,
      // Migrate deprecated persisted fields only when the new setting is absent.
      ...(supportsNtp
        ? {
            [ReportInputParameterKeys.ntpDisplayMode]: this.getNtpDisplayMode(
              settings[ReportDefaultSettingsKeys.ntpDisplayMode] ??
                (settings[ReportDefaultSettingsKeys.showNtpSeparately] === true
                  ? ReportNtpDisplayModes.separate
                  : settings[ReportDefaultSettingsKeys.showNtpSeparately] === false
                    ? ReportNtpDisplayModes.generalList
                    : ReportNtpDisplayModes.separate),
            ),
          }
        : {}),
      [ReportInputParameterKeys.showTotals]: settings[ReportDefaultSettingsKeys.showTotals] ?? true,
      ...(supportsSuppression
        ? {
            [ReportInputParameterKeys.showSuppressedAgents]:
              settings[ReportDefaultSettingsKeys.showSuppressedAgents] ??
              (settings[ReportDefaultSettingsKeys.hideSuppressedAgents] !== undefined
                ? !settings[ReportDefaultSettingsKeys.hideSuppressedAgents]
                : booleanDefaults[ReportInputParameterKeys.showSuppressedAgents]),
          }
        : {}),
    };

    if (settings[ReportDefaultSettingsKeys.startDate]) {
      variables[ReportInputParameterKeys.startDate] = settings[ReportDefaultSettingsKeys.startDate];
    }

    if (settings[ReportDefaultSettingsKeys.endDate]) {
      variables[ReportInputParameterKeys.endDate] = settings[ReportDefaultSettingsKeys.endDate];
    }

    this.patchState({
      templateDraft: {
        ...currentTemplate,
        title: settings[ReportDefaultSettingsKeys.title],
        subtitle: settings[ReportDefaultSettingsKeys.subtitle],
        rowsPerPage: settings[ReportDefaultSettingsKeys.rowsPerPage],
      },
      variables,
    });
    return settings;
  };

  public saveCurrentSettingsAsDefaults = async (reportId: ReportId, period?: ReportPeriod): Promise<boolean> => {
    const state = this.stateSig();
    const template = state.templateDraft;
    if (!template) {
      return false;
    }

    const existingSettings = await this.loadSavedDefaultSettings(reportId);
    const displayThreshold = this.toNonNegativeNumber(state.variables[ReportInputParameterKeys.displayThreshold], 0);
    const rowsPerPage = this.toPositiveInteger(template.rowsPerPage, 1);

    try {
      await this.reportDefaultSettingsService.save({
        [ReportDefaultSettingsKeys.reportId]: reportId as ReportIds,
        ...(period ? { [ReportDefaultSettingsKeys.period]: period } : {}),
        [ReportDefaultSettingsKeys.title]: template.title ?? '',
        [ReportDefaultSettingsKeys.subtitle]: template.subtitle ?? '',
        [ReportDefaultSettingsKeys.displayThreshold]: displayThreshold,
        [ReportDefaultSettingsKeys.includeThreshold]: this.isThresholdIncluded(state.variables),
        ...(reportSupportsInputParameter(reportId, ReportInputParameterKeys.ntpDisplayMode)
          ? {
              [ReportDefaultSettingsKeys.ntpDisplayMode]: this.getNtpDisplayMode(
                state.variables[ReportInputParameterKeys.ntpDisplayMode],
              ),
            }
          : {}),
        [ReportDefaultSettingsKeys.showTotals]: this.getRequiredBooleanVariableValue(
          state.variables[ReportInputParameterKeys.showTotals],
          'show_totals',
        ),
        ...(reportSupportsInputParameter(reportId, ReportInputParameterKeys.showSuppressedAgents)
          ? {
              [ReportDefaultSettingsKeys.showSuppressedAgents]: this.getRequiredBooleanVariableValue(
                state.variables[ReportInputParameterKeys.showSuppressedAgents],
                'show_suppressed_agents',
              ),
            }
          : {}),
        [ReportDefaultSettingsKeys.rowsPerPage]: rowsPerPage,
        [ReportDefaultSettingsKeys.startDate]:
          period && period !== 'custom' ? '' : String(state.variables[ReportInputParameterKeys.startDate] ?? ''),
        [ReportDefaultSettingsKeys.endDate]:
          period && period !== 'custom' ? '' : String(state.variables[ReportInputParameterKeys.endDate] ?? ''),
        [ReportDefaultSettingsKeys.isPublic]: existingSettings?.[ReportDefaultSettingsKeys.isPublic] ?? false,
      });

      return true;
    } catch {
      return false;
    }
  };

  private loadSavedDefaultSettings = async (reportId: ReportId) => {
    try {
      return await this.reportDefaultSettingsService.getByReportId(reportId);
    } catch {
      return null;
    }
  };

  private toNonNegativeNumber(value: unknown, fallback: number): number {
    if (value === undefined || value === null || value === '') {
      return fallback;
    }

    const numericValue = Number(value);
    if (!Number.isFinite(numericValue) || numericValue < 0) {
      return fallback;
    }

    return numericValue;
  }

  private toPositiveInteger(value: unknown, fallback: number): number {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue) || numericValue <= 0) {
      return fallback;
    }

    return Math.floor(numericValue);
  }

  private patchState = (patch: Partial<ProductionReportsState>): void => {
    this.state.update(state => ({
      ...state,
      ...patch,
    }));
  };

  private getErrorMessage = async (error: unknown): Promise<string> => {
    if (error instanceof HttpErrorResponse) {
      const backendMessage = await this.extractBackendMessage(error);
      if (error.status === 403) {
        return backendMessage ?? 'This report is not available for your account.';
      }
      return backendMessage ?? error.message;
    }

    if (error instanceof Error) {
      return error.message;
    }

    if (typeof error === 'string') {
      return error;
    }

    return 'Unknown error';
  };

  private extractBackendMessage = async (error: HttpErrorResponse): Promise<string | null> => {
    const body = error.error;
    try {
      const raw = body instanceof Blob ? await body.text() : body;
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (parsed && typeof parsed === 'object') {
        const message = (parsed as { message?: unknown }).message ?? (parsed as { error?: unknown }).error;
        return typeof message === 'string' ? message : null;
      }
    } catch {
      return null;
    }
    return null;
  };

  private getRequiredDateVariableValue(value: unknown, variableKey: string): string {
    if (value === undefined || value === null || value === '') {
      throw new Error(`${variableKey} is required`);
    }

    return String(value);
  }

  private getOptionalStringValue(value: string | undefined): string | undefined {
    if (typeof value !== 'string') {
      return undefined;
    }

    const normalizedValue = value.trim();
    return normalizedValue ? normalizedValue : undefined;
  }

  private getOptionalNumberVariableValue(value: unknown, variableKey: string): number | undefined {
    if (value === undefined || value === null || value === '') {
      return undefined;
    }

    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
      throw new Error(`${variableKey} must be a valid number`);
    }

    return numericValue;
  }

  private getRequiredPositiveIntegerValue(value: unknown, variableKey: string): number {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue) || numericValue <= 0) {
      throw new Error(`${variableKey} must be a positive integer`);
    }

    return Math.floor(numericValue);
  }

  private getNtpDisplayMode(value: unknown): ReportNtpDisplayModes {
    if (Object.values(ReportNtpDisplayModes).includes(value as ReportNtpDisplayModes)) {
      return value as ReportNtpDisplayModes;
    }

    throw new Error('ntp_display_mode must be hide, separate, or general_list');
  }

  /** Only an explicit off counts: settings and links from before the switch keep the threshold. */
  private isThresholdIncluded(variables: ProductionReportInputVariables): boolean {
    const value = variables[ReportInputParameterKeys.includeThreshold];

    return value !== false && value !== 'false';
  }

  private getRequiredBooleanVariableValue(value: unknown, variableKey: string): boolean {
    if (typeof value === 'boolean') {
      return value;
    }

    if (value === 'true') return true;
    if (value === 'false') return false;
    throw new Error(`${variableKey} must be a boolean`);
  }
}
